import type { MenuCategory, MenuProduct } from "@/mocks/branch/menu";
import ProductCard from "./ProductCard";

interface MenuBrowserProps {
  categories: MenuCategory[];
  products: MenuProduct[];
  activeCategory: string;
  search: string;
  onCategoryChange: (id: string) => void;
  onSearchChange: (value: string) => void;
  onAdd: (product: MenuProduct) => void;
}

export default function MenuBrowser({
  categories,
  products,
  activeCategory,
  search,
  onCategoryChange,
  onSearchChange,
  onAdd,
}: MenuBrowserProps) {
  const query = search.trim();
  const filtered = products.filter((p) => {
    if (p.available === false) return false;
    const inCategory = activeCategory === "all" || p.categoryId === activeCategory;
    const matchesSearch = query.length === 0 || p.name.includes(query);
    return inCategory && matchesSearch;
  });

  const tabs = [{ id: "all", name: "الكل" }, ...categories];

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-4 pt-4 pb-3 border-b border-background-200">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold text-foreground-900">طلب جديد</h2>
          <span className="text-xs text-foreground-400 font-medium">
            {filtered.length} منتج
          </span>
        </div>

        <div className="relative mt-3">
          <div className="absolute top-1/2 -translate-y-1/2 right-4 w-5 h-5 flex items-center justify-center text-foreground-400 pointer-events-none">
            <i className="ri-search-line text-lg"></i>
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="ابحث عن منتج..."
            className="w-full h-12 rounded-btn border border-background-300 bg-background-50 pr-12 pl-4 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 transition-all"
          />
        </div>

        <div className="flex gap-2 mt-3 overflow-x-auto pb-0.5 -mx-1 px-1">
          {tabs.map((tab) => {
            const active = activeCategory === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => onCategoryChange(tab.id)}
                className={`h-10 px-4 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer ${
                  active
                    ? "bg-primary-500 text-white"
                    : "bg-background-100 text-foreground-600 border border-background-300 hover:bg-background-200"
                }`}
              >
                {tab.name}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-16">
            <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-search-line text-2xl text-foreground-400"></i>
            </div>
            <p className="mt-3 text-sm font-semibold text-foreground-600">
              لا توجد منتجات مطابقة
            </p>
            <p className="mt-1 text-xs text-foreground-400">جرّب كلمة بحث أخرى</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {filtered.map((product) => (
              <ProductCard key={product.id} product={product} onAdd={onAdd} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}